import type { SharedStripeSetupIntent } from "@arbeidsark/shared"
import { Elements } from "@stripe/react-stripe-js"
import type { StripeElementsOptions } from "@stripe/stripe-js"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import styled, { useTheme } from "styled-components"
import { useTranslate } from "~/ur-react-hooks"
import { Loader } from "../../../components"
import { useLanguage } from "../../../locale/TranslationProvider"
import { stripeLocaleLookup } from "../../../utils/stripe"
import { trpc } from "../../../utils/trpc"
import { stripePromise } from "../stripe"
import SetupEnterPaymentDetails from "./SetupEnterPaymentDetails"

const Layout = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2rem;
`

const Card = styled.div`
  background: white;
  box-shadow: ${(props) => props.theme.shadow.elevation3};

  padding: 2rem 1.5rem;

  border-radius: 8px;

  width: 378px;
`

const CenterSpinner = styled.div`
  display: flex;
  justify-content: center;
`

export type ChangePaymentMethodProps = {}

const ChangePaymentMethod: React.FC<ChangePaymentMethodProps> = () => {
  const { mutate: createSetupIntent } =
    trpc.subscriptions.createSetupIntentForSubscription.useMutation({
      onSuccess(setupIntent) {
        setSetupIntent(setupIntent)
      },
    })

  const navigate = useNavigate()
  const [language] = useLanguage()
  const theme = useTheme()

  const [setupIntent, setSetupIntent] = useState<
    SharedStripeSetupIntent | undefined
  >()

  useEffect(() => {
    if (!setupIntent) {
      createSetupIntent()
    }
  }, [createSetupIntent, setupIntent])

  const translations = useTranslate({
    title: "change-payment-method.title",
    submitText: "change-payment-method.submit-text",
  })

  if (!setupIntent) {
    return (
      <Layout>
        <Card>
          <CenterSpinner>
            <Loader.Spinner />
          </CenterSpinner>
        </Card>
      </Layout>
    )
  }

  const options: StripeElementsOptions = {
    clientSecret: setupIntent.clientSecret,
    appearance: {
      rules: {
        ".Input": {
          borderColor: theme.colors.gray70,
          borderRadius: "4px",
          outline: "none",
        },
        ".Input:focus": {
          border: "2px solid " + theme.colors.primary40,
          boxShadow: "none",
        },
      },
    },
    locale: stripeLocaleLookup[language],
  }

  return (
    <Elements stripe={stripePromise} options={options}>
      <Layout>
        <Card>
          <SetupEnterPaymentDetails
            title={translations.title}
            submitText={translations.submitText}
            redirectUrl={`${
              import.meta.env.VITE_APP_URL
            }/subscription/change-payment-method-success`}
            onBack={() => {
              navigate("/subscription")
            }}
          />
        </Card>
      </Layout>
    </Elements>
  )
}

export default ChangePaymentMethod
